import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { catchError, of, Subscription, take } from 'rxjs';
import { LienReleaseTemplate } from '../../../domains/invoices/lien-release-template/lien-release-template.model';
import { RetainageService } from '../../../domains/retainage/retainage.service';
import { WorkOrderStatus } from '../../../domains/work/work-order/work-order.model';
import { WorkOrderForm, WorkOrderTagForm } from '../draft-work-order.form';

/**
 * TT-260 item 4. The VTI threshold is chosen by rank, which is how people
 * talk about installers, instead of the old 0 to 5 number. Each rank's floor
 * is the lowest VTI that earns it, using the same bands the backend scores with
 * (VtiRatingService.getRank), so "Expert" means "Expert or higher".
 */
export const VTI_RANK_FLOORS = [
  { name: 'Rookie', floor: 0 },
  { name: 'Intermediate', floor: 0.25 },
  { name: 'Experienced', floor: 0.75 },
  { name: 'Proficient', floor: 1.5 },
  { name: 'Expert', floor: 2.5 },
  { name: 'Master', floor: 3.5 },
  { name: 'Artisan', floor: 4.5 },
  { name: 'Elite Artisan', floor: 5.25 },
];

/** The rank a threshold falls in: the last one whose floor it reaches. */
export const rankIndexFor = (threshold?: number | null): number => {
  const value = Number(threshold ?? 0);
  let index = 0;
  VTI_RANK_FLOORS.forEach((rank, i) => {
    if (value >= rank.floor) index = i;
  });
  return index;
};

@Component({
  selector: 'gc-publishing-step',
  templateUrl: './publishing-step.component.html',
  styleUrls: ['./publishing-step.component.scss'],
})
export class PublishingStepComponent implements OnInit, OnDestroy {
  @Input() form!: WorkOrderForm;
  @Input() lienReleaseTemplates!: LienReleaseTemplate[];

  tagForm = new WorkOrderTagForm();
  readonly ranks = VTI_RANK_FLOORS;
  readonly rankIndex = new FormControl<number>(0, { nonNullable: true });
  private rankChanges?: Subscription;

  constructor(private readonly retainage: RetainageService) {}

  ngOnInit(): void {
    this.rankIndex.setValue(rankIndexFor(this.form.minimumHammerRatingThreshold.value), { emitEvent: false });
    this.rankChanges = this.rankIndex.valueChanges.subscribe(index => {
      const rank = VTI_RANK_FLOORS[index] ?? VTI_RANK_FLOORS[0];
      this.form.minimumHammerRatingThreshold.setValue(rank.floor);
      this.form.minimumHammerRatingThreshold.markAsDirty();
    });
    this.prefillRetainage();
  }

  /**
   * TT-260: a draft that has no retainage yet starts with the company's
   * default, shown here so the company sees what it is agreeing to. Silent if
   * the API is older or the company has no default.
   */
  private prefillRetainage(): void {
    const control = this.form.retainagePercent;
    if (this.form.workOrderStatus !== WorkOrderStatus.Draft || control.dirty || (control.value !== null && control.value !== undefined)) {
      return;
    }
    this.retainage
      .settings()
      .pipe(
        take(1),
        catchError(() => of({ defaultPercent: null })),
      )
      .subscribe(({ defaultPercent }) => {
        if (defaultPercent && control.pristine && (control.value === null || control.value === undefined)) {
          control.setValue(defaultPercent);
          control.markAsDirty();
        }
      });
  }

  ngOnDestroy(): void {
    this.rankChanges?.unsubscribe();
  }

  addTag() {
    if (this.tagForm.invalid) {
      return;
    }

    this.form.addTag(this.tagForm.updatedModel);
    this.tagForm.reset();
  }

  removeTag(index: number) {
    this.form.removeTagAt(index);
  }

  /** The pin on the slider reads the rank, not a number. */
  public rankName(index: number) {
    return VTI_RANK_FLOORS[index]?.name ?? '';
  }
}
